import { useEffect, useRef, useState } from "react";
import { api, fmtDateTime, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, Download, Unlink, History, Eye, FolderOpen, RefreshCw } from "lucide-react";
import { KalkulationFileRow } from "@/components/KalkulationFileRow";
import { FileThumb } from "@/components/FileThumb";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import { ReuseCustomerFilesDialog } from "@/components/ReuseCustomerFilesDialog";
import { ACCEPT, KIND_LABEL, MAX_BYTES, downloadFile, fmtSize, uploadMessage } from "@/lib/files";
import { SectionBody, SectionHead, SectionSummary, useSection } from "@/components/SectionCollapse";

const STATUS_LABEL = {
  neu: "Neu",
  freigegeben: "Freigegeben",
  in_bearbeitung: "In Bearbeitung",
  fertig: "Fertig",
  verworfen: "Verworfen",
};
const STATUS_COLOR = {
  neu: "bg-slate-100 text-slate-700 border-slate-300",
  freigegeben: "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_bearbeitung: "bg-blue-50 text-blue-700 border-blue-200",
  fertig: "bg-indigo-50 text-indigo-700 border-indigo-200",
  verworfen: "bg-rose-50 text-rose-700 border-rose-200",
};
const UNLINK_ROLES = ["admin", "manager", "employee"];

export default function AttachmentsSection({ orderId, kundeId = null, positionId = null, readOnly = false, title = "Dateien", calc = null, collapseKey }) {
  const [items, setItems] = useState([]);
  const [showOld, setShowOld] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState("kundendatei");
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reuseOpen, setReuseOpen] = useState(false);
  const fileInput = useRef(null);
  const canUnlink = !readOnly && UNLINK_ROLES.includes(getUser()?.rolle);
  const section = useSection(collapseKey);

  const load = async () => {
    try {
      const params = { include_old_versions: showOld };
      if (positionId) params.position_id = positionId;
      const r = await api.get(`/auftraege/${orderId}/attachments`, { params });
      setItems(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (orderId) load(); }, [orderId, positionId, showOld]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > MAX_BYTES) { toast.error(`${file.name}: Datei überschreitet 100 MB.`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        if (positionId) fd.append("position_id", positionId);
        try {
          const r = await api.post(`/auftraege/${orderId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          toast.success(uploadMessage(file.name, r.data));
        } catch (e) {
          toast.error(`${file.name}: ${e?.response?.data?.detail || "Fehlgeschlagen."}`);
        }
      }
      await load();
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (readOnly) return;
    upload(Array.from(e.dataTransfer.files || []));
  };

  const changeKind = async (id, newKind) => {
    try { await api.patch(`/attachments/${id}`, { kind: newKind }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };
  const changeStatus = async (id, newStatus) => {
    try { await api.patch(`/attachments/${id}`, { status: newStatus }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };
  const unlink = async (a) => {
    const hint = kundeId ? " Die Datei bleibt beim Kunden unter „Dateien“ erhalten." : "";
    if (!window.confirm(`„${a.original_name}“ aus diesem Auftrag entfernen?${hint}`)) return;
    try {
      const r = await api.delete(`/attachments/${a.id}`);
      await load();
      toast.success(r.data?.kept_as_customer_file ? "Aus dem Auftrag entfernt – Datei bleibt beim Kunden erhalten." : "Datei entfernt.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
  };
  const download = (a) => downloadFile(a).catch(() => toast.error("Download fehlgeschlagen."));
  const switchLatest = async (a, force = false) => {
    try {
      const r = await api.post(`/auftraege/${orderId}/attachments/${a.id}/neueste-version`, null, { params: force ? { force: true } : {} });
      toast.success(`Auf Version ${r.data.version} umgestellt.`);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Umstellen fehlgeschlagen.";
      if (e?.response?.status === 409 && !force) {
        if (window.confirm(`${detail}\n\nTrotzdem auf die neueste Version umstellen?`)) await switchLatest(a, true);
      } else toast.error(detail);
    }
  };

  return (
    <div className="space-y-3" data-testid={positionId ? `pos-attachments-${positionId}` : "order-attachments"}>
      <SectionHead k={collapseKey} label={title} className="flex items-center gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        {!section.collapsed && <Badge className="bg-slate-100 text-slate-700 border-slate-300" variant="outline">{items.length}</Badge>}
        <SectionSummary k={collapseKey}>{items.length === 1 ? "1 Datei" : `${items.length} Dateien`}</SectionSummary>
        <div className="ml-auto flex items-center gap-2">
          {!readOnly && kundeId && !positionId && (
            <Button size="sm" variant="outline" onClick={() => setReuseOpen(true)} data-testid="attachments-reuse-button">
              <FolderOpen className="w-4 h-4 mr-1" /> Vorhandene Kundendateien verwenden
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => setShowOld((v) => !v)} data-testid="attachments-toggle-versions">
            <History className="w-4 h-4 mr-1" /> {showOld ? "Nur aktuelle" : "Alle Versionen"}
          </Button>
        </div>
      </SectionHead>

      <SectionBody k={collapseKey} className="space-y-3">
      {!readOnly && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors ${dragOver ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50"}`}
          data-testid="attachments-dropzone"
        >
          <Upload className="w-7 h-7 mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-600 mb-3">Dateien hierhin ziehen oder klicken zum Auswählen</p>
          <p className="text-xs text-slate-500 mb-3">STL · STEP · 3MF · OBJ · PDF · JPG · PNG · GLTF · GLB · max. 100 MB</p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger className="w-44" data-testid="attachments-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(KIND_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <input ref={fileInput} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="attachments-file-input" />
            <Button size="sm" onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="attachments-upload-button">
              {uploading ? "Lädt hoch…" : "Datei auswählen"}
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-2">Noch keine Dateien.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((a) => (
            <div key={a.id} className="flex gap-3 p-3 border border-slate-200 rounded-lg bg-white hover:border-slate-300 transition-colors" data-testid={`attachment-${a.id}`}>
              <button onClick={() => setPreview(a)} className="rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" aria-label={`Vorschau ${a.original_name}`} data-testid={`attachment-thumb-${a.id}`}>
                <FileThumb att={a} />
              </button>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-baseline gap-2">
                  <button onClick={() => setPreview(a)} className="text-sm font-medium text-slate-900 truncate hover:text-blue-600 text-left focus:outline-none focus-visible:underline" title={`${a.original_name} – Vorschau öffnen`} data-testid={`attachment-name-${a.id}`}>
                    {a.original_name}
                  </button>
                  {a.version > 1 && <Badge className="bg-slate-100 text-slate-700 border-slate-300 text-xs" variant="outline">v{a.version}</Badge>}
                </div>
                {a.newer_version && (
                  <div className="flex items-center gap-2 flex-wrap" data-testid={`attachment-newer-${a.id}`}>
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-xs" data-testid={`attachment-newer-badge-${a.id}`}>Neuere Version verfügbar (v{a.newer_version})</Badge>
                    {!readOnly && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => switchLatest(a)} data-testid={`attachment-switch-latest-${a.id}`}>
                        <RefreshCw className="w-3.5 h-3.5 mr-1" /> Auf neueste Version umstellen
                      </Button>
                    )}
                  </div>
                )}
                <div className="text-xs text-slate-500">{fmtSize(a.size)} · {a.ext.toUpperCase()} · {fmtDateTime(a.created_at)}{a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ""}</div>
                <div className="flex items-center gap-2 flex-wrap pt-1">
                  {!readOnly ? (
                    <>
                      <Select value={a.kind} onValueChange={(v) => changeKind(a.id, v)}>
                        <SelectTrigger className="h-7 w-32 text-xs" data-testid={`attachment-kind-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(KIND_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={a.status} onValueChange={(v) => changeStatus(a.id, v)}>
                        <SelectTrigger className={`h-7 w-32 text-xs ${STATUS_COLOR[a.status]}`} data-testid={`attachment-status-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                      </Select>
                    </>
                  ) : (
                    <>
                      <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300">{KIND_LABEL[a.kind]}</Badge>
                      <Badge variant="outline" className={STATUS_COLOR[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                    </>
                  )}
                </div>
                <KalkulationFileRow att={a} calc={calc} />
              </div>
              <div className="flex-shrink-0 flex flex-col gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setPreview(a)} data-testid={`attachment-preview-${a.id}`} title="Vorschau">
                  <Eye className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(a)} data-testid={`attachment-download-${a.id}`} title="Herunterladen">
                  <Download className="w-4 h-4" />
                </Button>
                {canUnlink && (
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => unlink(a)} data-testid={`attachment-delete-${a.id}`} title="Aus Auftrag entfernen">
                    <Unlink className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      </SectionBody>
      <FilePreviewDialog att={preview} onClose={() => setPreview(null)} />
      {kundeId && <ReuseCustomerFilesDialog open={reuseOpen} onClose={() => setReuseOpen(false)} orderId={orderId} kundeId={kundeId} onLinked={load} />}
    </div>
  );
}
